import api from './api';

// Types pour les types de notifications
export type NotificationType = 
  | 'intervention_request'
  | 'intervention_assigned'
  | 'intervention_completed'
  | 'intervention_updated'
  | 'complaint_created'
  | 'complaint_response'
  | 'subscription_created'
  | 'subscription_expiring'
  | 'order_created'
  | 'order_status_update'
  | 'quote_created'
  | 'quote_accepted'
  | 'quote_rejected'
  | 'quote_updated'
  | 'contract_created'
  | 'contract_activated'
  | 'contract_expiring'
  | 'payment_received'
  | 'report_submitted'
  | 'report_confirmation_required'
  | 'maintenance_offer_created'
  | 'maintenance_offer_activated'
  | 'general';

export type NotificationPriority = 'low' | 'medium' | 'high' | 'urgent';

// Interface pour une notification
export interface Notification {
  id: number;
  user_id: number;
  type: NotificationType;
  title: string;
  message: string;
  data?: any;
  is_read: boolean;
  read_at?: string;
  priority: NotificationPriority;
  action_url?: string;
  actionUrl?: string; // Alias pour action_url
  created_at: string;
  createdAt?: string;
  updated_at: string;
}

// Interface pour les filtres de recherche
export interface NotificationFilters {
  unread_only?: boolean;
  type?: NotificationType;
  limit?: number;
  offset?: number;
}

// Interface pour la réponse paginée
interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

// Interface pour la réponse d'une seule notification
interface NotificationResponse {
  success: boolean;
  data: Notification;
}

// Interface pour le comptage
interface CountResponse {
  success: boolean;
  data: { count: number };
}


/**
 * Service pour la gestion des notifications
 */
class NotificationService {
  /**
   * Récupère la liste des notifications de l'utilisateur connecté
   */
  async getMyNotifications(filters: NotificationFilters = {}): Promise<PaginatedResponse<Notification>> {
    try {
      const response = await api.get<PaginatedResponse<Notification>>('/notifications', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des notifications:', error);
      throw new Error('Impossible de charger les notifications.');
    }
  }

  /**
   * Récupère le nombre de notifications non lues
   */
  async getUnreadCount(): Promise<number> {
    try {
      const response = await api.get<CountResponse>('/notifications/unread-count');
      return response.data.data.count;
    } catch (error) {
      console.error('Erreur lors du comptage des notifications:', error);
      return 0;
    }
  }


  /**
   * Marque une notification comme lue
   */
  async markAsRead(id: number): Promise<void> {
    try {
      await api.patch(`/notifications/${id}/read`);
    } catch (error) {
      console.error(`Erreur lors du marquage de la notification ${id}:`, error);
      throw new Error('Impossible de marquer la notification comme lue.');
    }
  }

  /**
   * Marque une notification comme non lue
   */
  async markAsUnread(id: number): Promise<void> {
    try {
      await api.patch(`/notifications/${id}/unread`);
    } catch (error) {
      console.error(`Erreur lors du marquage de la notification ${id}:`, error);
      throw new Error('Impossible de marquer la notification comme non lue.');
    }
  }

  /**
   * Marque toutes les notifications comme lues
   */
  async markAllAsRead(): Promise<void> {
    try {
      await api.post('/notifications/mark-all-read');
    } catch (error) {
      console.error('Erreur lors du marquage de toutes les notifications:', error);
      throw new Error('Impossible de marquer toutes les notifications comme lues.');
    }
  }

  /**
   * Supprime une notification
   */
  async deleteNotification(id: number): Promise<void> {
    try {
      await api.delete(`/notifications/${id}`);
    } catch (error) {
      console.error(`Erreur lors de la suppression de la notification ${id}:`, error);
      throw new Error('Impossible de supprimer la notification.');
    }
  }

  /**
   * Supprime toutes les notifications de l'utilisateur
   */
  async deleteAllNotifications(): Promise<{ count: number }> {
    try {
      const response = await api.delete('/notifications/delete-all');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression de toutes les notifications:', error);
      throw new Error('Impossible de supprimer toutes les notifications.');
    }
  }

}

// Export d'une instance unique du service
export const notificationService = new NotificationService();
export default notificationService;
